/**
 * Electron Provider - Web Stub
 * Provides compatible API surface for components shared with desktop
 * All Electron-specific functionality is stubbed out for web
 */

import { createContext, useContext } from 'react';

// ============================================================================
// TYPE DEFINITIONS (Match desktop for compatibility)
// ============================================================================

export type ScreenSource = {
  id: string;
  name: string;
  thumbnail?: string;
  display_id?: string;
  display_name?: string;
};

export type CaptureOptions = {
  sourceId: string;
  constraints?: {
    width?: number;
    height?: number;
  };
  options?: {
    includeCursor?: boolean;
    format?: 'png' | 'jpeg';
    quality?: number;
  };
};

export type CaptureResult = {
  dataUrl: string;
  width: number;
  height: number;
  timestamp: number;
};

export type QuickCaptureResult = {
  success: boolean;
  dataUrl?: string;
  width?: number;
  height?: number;
  timestamp: number;
  error?: string;
};

export type PermissionStatus = {
  screen: boolean;
};

export type PermissionCheckResult = {
  hasPermission: boolean;
  status: string;
  platform: string;
  error?: string;
};

export type WindowBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ExternalLinkOptions = {
  width?: number;
  height?: number;
  title?: string;
  modal?: boolean;
};

export type ExternalLinkResult = {
  success: boolean;
  windowId?: number;
  error?: string;
};

// ============================================================================
// ELECTRON CONTEXT TYPE (Complete stub)
// ============================================================================

type ElectronContextType = {
  isElectronAvailable: boolean;
  isInitializing: boolean;
  initError: Error | null;

  // Window controls - Not available in web
  windowControls: {
    minimize: () => Promise<void>;
    maximize: () => Promise<void>;
    close: () => Promise<void>;
    isMaximized: () => Promise<boolean>;
    getBounds: () => Promise<WindowBounds>;
    setContentSize: (
      width: number,
      height: number
    ) => Promise<{ success: boolean; error?: string }>;
    hide: () => Promise<{ success: boolean; error?: string }>;
    show: () => Promise<{ success: boolean; error?: string }>;
    toggleVisibility: () => Promise<{ success: boolean; error?: string }>;
    isVisible: () => Promise<boolean>;
    onVisibilityChanged: (callback: (event: any) => void) => () => void;
  };

  // Screen capture - Extension handles this in web
  screenCaptureControls: {
    openSystemSettings: () => Promise<{ success: boolean; error?: string }>;
    getSources: () => Promise<ScreenSource[]>;
    capture: (options: CaptureOptions) => Promise<CaptureResult>;
    quickCapture: (displayId: string | undefined) => Promise<QuickCaptureResult>;
    requestPermissions: () => Promise<PermissionStatus>;
    hasPermissions: () => Promise<PermissionStatus>;
    checkPermissions: () => Promise<PermissionCheckResult>;
    forcePermissionsDialog: () => Promise<{
      success: boolean;
      sourcesFound?: number;
      message: string;
      error?: string;
    }>;
    onPermissionChanged: (callback: (event: any) => void) => () => void;
  };

  // Display controls - Not available in web
  displayControls: {
    getAllDisplays: () => Promise<any[]>;
    getPrimaryDisplay: () => Promise<any>;
    getDisplayById: (displayId: string) => Promise<any>;
  };

  // Hotkeys - Extension handles this in web
  hotkeys: {
    register: (eventId: string, callback: (event: any) => void) => Promise<boolean>;
    unregister: (eventId?: string) => Promise<void>;
    isRegistered: (eventId: string) => Promise<boolean>;
    getConflicts: () => Promise<string[]>;
  };

  // Auth - Web has its own auth system
  auth: {
    onCallbackReceived: (callback: (data: any) => void) => void;
    removeCallbackListeners: () => void;
    storeTokens: (
      accessToken: string,
      refreshToken: string,
      expiresAt: number
    ) => Promise<{ success: boolean; error?: string }>;
    getTokens: () => Promise<{
      success: boolean;
      tokens?: { accessToken: string; refreshToken: string; expiresAt: number } | null;
      error?: string;
    }>;
    clearToken: () => Promise<{ success: boolean; error?: string }>;
  };

  // PandaDoc - Web opens in new tab
  pandaDoc: {
    openSigningSession: (url: string) => Promise<{ success: boolean; error?: string }>;
    onCallbackReceived: (callback: (data: any) => void) => () => void;
    removeCallbackListeners: () => void;
  };

  // Billing - Web opens in new tab
  billing: {
    openCustomerPortal: () => Promise<{ success: boolean; error?: string }>;
    onCallbackReceived: (callback?: (data?: any) => void) => () => void;
    removeCallbackListeners: () => void;
  };

  // Network - Browser has its own network detection
  network: {
    getConnectionStatus: () => Promise<{ isOnline: boolean; timestamp: number }>;
    testConnectivity: (
      testUrl: string,
      timeout?: number
    ) => Promise<{ success: boolean; latency?: number; error?: string }>;
    getNetworkInfo: () => Promise<any>;
    onNetworkChange: (callback: (networkData: any) => void) => () => void;
    onOnline: (callback: (data: any) => void) => () => void;
    onOffline: (callback: (data: any) => void) => () => void;
  };

  // Environment - Web uses import.meta.env
  env: {
    getOpenAIApiKey: () => string | null;
  };

  // External links - Browser compatible
  external: {
    openLink: (url: string, options?: ExternalLinkOptions) => Promise<ExternalLinkResult>;
    openInBrowser: (url: string) => Promise<{ success: boolean; error?: string }>;
  };

  // System - Not available in web
  system: {
    setLaunchOnStartup: (enabled: boolean) => Promise<{ success: boolean; error?: string }>;
    getLaunchOnStartup: () => Promise<{ success: boolean; enabled: boolean; error?: string }>;
  };

  // Updates - Not available in web (browser auto-updates)
  update: {
    checkForUpdates: () => Promise<{ success: boolean; error?: string }>;
    quitAndInstall: () => Promise<{ success: boolean; error?: string }>;
    installUpdate: (
      installationType?: 'appimage' | 'deb' | 'rpm'
    ) => Promise<{ success: boolean; error?: string }>;
    onCheckingForUpdate: (callback: () => void) => void;
    onUpdateAvailable: (callback: (info: any) => void) => void;
    onUpdateNotAvailable: (callback: () => void) => void;
    onDownloadProgress: (callback: (progress: any) => void) => void;
    onUpdateDownloaded: (callback: () => void) => void;
    onError: (callback: (error: any) => void) => void;
    removeUpdateListeners: () => void;
  };

  // Logger - Web uses console
  logger: {
    initFileTransport: (options: any) => Promise<{ success: boolean; error?: string }>;
    writeLog: (message: string) => Promise<{ success: boolean; error?: string }>;
    getLogFilePath: (
      filePrefix: 'client' | 'main'
    ) => Promise<{ success: boolean; error?: string }>;
    readLogsTimeRange: (options: any) => Promise<{ success: boolean; error?: string }>;
    shutdown: () => Promise<{ success: boolean; error?: string }>;
  };
};

// ============================================================================
// DEFAULT CONTEXT VALUE (All stubs/no-ops)
// ============================================================================

const defaultValue: ElectronContextType = {
  isElectronAvailable: false,
  isInitializing: false,
  initError: null,

  windowControls: {
    minimize: async () => {},
    maximize: async () => {},
    close: async () => {},
    isMaximized: async () => false,
    getBounds: async () => ({ x: 0, y: 0, width: 800, height: 600 }),
    setContentSize: async () => ({ success: false, error: 'Not available in web' }),
    hide: async () => ({ success: false, error: 'Not available in web' }),
    show: async () => ({ success: false, error: 'Not available in web' }),
    toggleVisibility: async () => ({ success: false, error: 'Not available in web' }),
    isVisible: async () => true,
    onVisibilityChanged: () => () => {},
  },

  screenCaptureControls: {
    openSystemSettings: async () => ({
      success: false,
      error: 'Screen capture handled by extension in web',
    }),
    getSources: async () => [],
    capture: async () => {
      throw new Error('Screen capture handled by extension in web');
    },
    quickCapture: async () => ({
      success: false,
      timestamp: Date.now(),
      error: 'Screen capture handled by extension in web',
    }),
    requestPermissions: async () => ({ screen: false }),
    hasPermissions: async () => ({ screen: false }),
    checkPermissions: async () => ({
      hasPermission: false,
      status: 'not-determined',
      platform: 'web',
      error: 'Screen capture handled by extension',
    }),
    forcePermissionsDialog: async () => ({
      success: false,
      message: 'Screen capture handled by extension in web',
    }),
    onPermissionChanged: () => () => {},
  },

  displayControls: {
    getAllDisplays: async () => [],
    getPrimaryDisplay: async () => ({}),
    getDisplayById: async () => ({}),
  },

  hotkeys: {
    register: async () => false,
    unregister: async () => {},
    isRegistered: async () => false,
    getConflicts: async () => [],
  },

  auth: {
    onCallbackReceived: () => {},
    removeCallbackListeners: () => {},
    storeTokens: async () => ({ success: false, error: 'Auth handled differently in web' }),
    getTokens: async () => ({ success: false, error: 'Auth handled differently in web' }),
    clearToken: async () => ({ success: false, error: 'Auth handled differently in web' }),
  },

  pandaDoc: {
    openSigningSession: async (url: string) => {
      window.open(url, '_blank', 'noopener,noreferrer');
      return { success: true };
    },
    onCallbackReceived: () => () => {},
    removeCallbackListeners: () => {},
  },

  billing: {
    openCustomerPortal: async () => {
      // Web app should open Stripe portal directly
      return { success: false, error: 'Open portal directly from web UI' };
    },
    onCallbackReceived: () => () => {},
    removeCallbackListeners: () => {},
  },

  network: {
    getConnectionStatus: async () => ({
      isOnline: navigator.onLine,
      timestamp: Date.now(),
    }),
    testConnectivity: async () => ({ success: false, error: 'Not available in web' }),
    getNetworkInfo: async () => ({ interfaces: [], hasWifi: false, hasEthernet: false }),
    onNetworkChange: () => () => {},
    onOnline: () => () => {},
    onOffline: () => () => {},
  },

  env: {
    getOpenAIApiKey: () => import.meta.env.VITE_OPENAI_API_KEY || null,
  },

  external: {
    openLink: async (url: string, options?: ExternalLinkOptions) => {
      const features = options
        ? `width=${options.width || 800},height=${options.height || 600}`
        : 'noopener,noreferrer';
      window.open(url, '_blank', features);
      return { success: true };
    },
    openInBrowser: async (url: string) => {
      window.open(url, '_blank', 'noopener,noreferrer');
      return { success: true };
    },
  },

  system: {
    setLaunchOnStartup: async () => ({ success: false, error: 'Not available in web' }),
    getLaunchOnStartup: async () => ({ success: false, enabled: false }),
  },

  update: {
    checkForUpdates: async () => ({ success: false, error: 'Browser handles updates' }),
    quitAndInstall: async () => ({ success: false, error: 'Not available in web' }),
    installUpdate: async () => ({ success: false, error: 'Not available in web' }),
    onCheckingForUpdate: () => {},
    onUpdateAvailable: () => {},
    onUpdateNotAvailable: () => {},
    onDownloadProgress: () => {},
    onUpdateDownloaded: () => {},
    onError: () => {},
    removeUpdateListeners: () => {},
  },

  logger: {
    initFileTransport: async () => ({ success: false, error: 'Not available in web' }),
    writeLog: async () => ({ success: false, error: 'Use console in web' }),
    getLogFilePath: async () => ({ success: false, error: 'Not available in web' }),
    readLogsTimeRange: async () => ({ success: false, error: 'Not available in web' }),
    shutdown: async () => ({ success: true }),
  },
};

// ============================================================================
// CONTEXT & HOOK
// ============================================================================

const ElectronContext = createContext<ElectronContextType>(defaultValue);

/**
 * Hook to access Electron API stubs
 * In web, all Electron-specific features are stubbed out
 */
export function useElectron(): ElectronContextType {
  const context = useContext(ElectronContext);
  return context || defaultValue;
}

/**
 * Provider component (not needed in web, but kept for compatibility)
 */
export const ElectronProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <ElectronContext.Provider value={defaultValue}>{children}</ElectronContext.Provider>;
};
